'use client';

import { c as _c } from "react/compiler-runtime";
import type { Data } from '@repo/strapi-types';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import { BillingDetails } from '@/components/page-builder/global/pricingV1/overview/BillingDetails';
import { PlanHighlights } from '@/components/page-builder/global/pricingV1/overview/PlanHighlights';
import { PlanCallToAction } from '@/components/page-builder/global/pricingV1/shared/PlanCallToAction';
import { getColsClassName } from '@/components/page-builder/global/pricingV1/utils/table-helper';
import Heading from '@/components/typography/Heading';
import { Paragraph } from '@/components/typography/Paragraph';
import { PRICING_CTA_SECTIONS, SITE_HEADER_OFFSET_DESKTOP, STICKY_HEADER_BOTTOM_THRESHOLD, STICKY_HEADER_FADE_DISTANCE, STICKY_HEADER_FADE_THRESHOLD } from '@/constants/pricing.constants';
import { useRefSticky } from '@/hooks/useRefSticky';
import { useScrollFadeOut } from '@/hooks/useScrollFadeOut';
import { cn } from '@/lib/styles';
import { PRICING_TEST_IDS } from '@/lib/test-ids';
export function OverviewTable(t0) {
  const $ = _c(70);
  const {
    data,
    currentTab,
    yearly: t1,
    className,
    stickyTopOffset: t2
  } = t0;
  const yearly = t1 === undefined ? false : t1;
  const stickyTopOffset = t2 === undefined ? SITE_HEADER_OFFSET_DESKTOP : t2;
  const t = useTranslations("Pricing");
  const tableHeaderRef = useRef(null);
  const containerRef = useRef(null);
  const {
    isSticky,
    isWithinBottomThreshold
  } = useRefSticky(tableHeaderRef, stickyTopOffset, STICKY_HEADER_BOTTOM_THRESHOLD);
  const opacity = useScrollFadeOut(containerRef, STICKY_HEADER_FADE_THRESHOLD, STICKY_HEADER_FADE_DISTANCE);
  let plansData;
  let rowClassName;
  let t10;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== className || $[1] !== currentTab || $[2] !== data || $[3] !== isSticky || $[4] !== opacity || $[5] !== t || $[6] !== yearly) {
    const gridColsClassName = !currentTab ? getColsClassName(data?.length) : "grid-cols-1";
    rowClassName = cn("grid divide-x divide-neutral-700", gridColsClassName);
    let t11;
    if ($[17] !== currentTab || $[18] !== data) {
      t11 = currentTab ? data?.filter(item => item.title === currentTab) : data;
      $[17] = currentTab;
      $[18] = data;
      $[19] = t11;
    } else {
      t11 = $[19];
    }
    plansData = t11;
    t7 = containerRef;
    const t12 = !yearly && "pt-15";
    if ($[20] !== className || $[21] !== t12) {
      t8 = cn("relative pt-5 transition-opacity duration-200", t12, className);
      $[20] = className;
      $[21] = t12;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    if ($[23] !== opacity) {
      t9 = {
        opacity
      };
      $[23] = opacity;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    t10 = PRICING_TEST_IDS.LIST_CONTENT;
    t4 = tableHeaderRef;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = cn("grid lg:sticky lg:top-(--site-header-offset-desktop) lg:z-2 lg:pt-5 bg-brand-black");
      $[25] = t5;
    } else {
      t5 = $[25];
    }
    t6 = yearly ? <div className={cn("hidden lg:grid bg-brand-black", gridColsClassName)}>{plansData?.map(item_0 => <div key={`${item_0?.id}-popular-header`} className={cn("p-2 grid place-items-center", item_0.mostPopular && "bg-neutral-light-100 text-brand-black rounded-t-lg lg:-ml-px lg:pl-px")}>{item_0.mostPopular ? <Paragraph fontSize="bodyBoldMedium" className="uppercase">{t("mostPopular")}</Paragraph> : null}</div>)}</div> : null;
    t3 = cn(rowClassName, isSticky && "lg:border-b lg:border-b-neutral-700 lg:transition-[colors,opacity] lg:duration-300");
    $[0] = className;
    $[1] = currentTab;
    $[2] = data;
    $[3] = isSticky;
    $[4] = opacity;
    $[5] = t;
    $[6] = yearly;
    $[7] = plansData;
    $[8] = rowClassName;
    $[9] = t10;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    plansData = $[7];
    rowClassName = $[8];
    t10 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
  }
  let t11;
  if ($[26] !== isSticky || $[27] !== isWithinBottomThreshold || $[28] !== plansData) {
    t11 = plansData?.map(t12 => {
      const {
        id,
        mostPopular,
        title
      } = t12;
      return <div key={`${id}-titles`} className={cn("px-3.75 pt-7.5 lg:pt-5 pb-2.5 border-neutral-700 relative", "border-t first:rounded-tl-xl last:rounded-tr-xl first:border-l last:border-r", mostPopular && "pl-4 pr-3.75 bg-surface-featured lg:-ml-px lg:border-l")}><Heading tag="h2" className="w-fit text-neutral-light-100" fontSize="headingMedium">{title}</Heading><div className={cn("absolute -bottom-6.25 left-0 right-0 h-6 hidden lg:block", "bg-linear-to-b from-brand-black to-transparent", mostPopular ? "from-surface-featured" : "from-brand-black", isSticky && !isWithinBottomThreshold ? "opacity-100" : "opacity-0", "transition-opacity duration-200")} /></div>;
    });
    $[26] = isSticky;
    $[27] = isWithinBottomThreshold;
    $[28] = plansData;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11 || $[31] !== t3) {
    t12 = <div className={t3}>{t11}</div>;
    $[30] = t11;
    $[31] = t3;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t12 || $[34] !== t4 || $[35] !== t5 || $[36] !== t6) {
    t13 = <div ref={t4} className={t5}>{t6}{t12}</div>;
    $[33] = t12;
    $[34] = t4;
    $[35] = t5;
    $[36] = t6;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== plansData) {
    t14 = plansData?.map(_temp);
    $[38] = plansData;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== rowClassName || $[41] !== t14) {
    t15 = <div className={rowClassName}>{t14}</div>;
    $[40] = rowClassName;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== plansData || $[44] !== yearly) {
    t16 = plansData?.map(item_1 => <BillingDetails key={`${item_1?.id}-billing-details`} pricingItem={item_1} yearly={yearly} className={cn("border-neutral-700 first:border-l last:border-r", item_1.mostPopular && "bg-surface-featured")} />);
    $[43] = plansData;
    $[44] = yearly;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== rowClassName || $[47] !== t16) {
    t17 = <div className={rowClassName}>{t16}</div>;
    $[46] = rowClassName;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== plansData) {
    t18 = plansData?.map(_temp2);
    $[49] = plansData;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== rowClassName || $[52] !== t18) {
    t19 = <div className={rowClassName}>{t18}</div>;
    $[51] = rowClassName;
    $[52] = t18;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== plansData || $[55] !== yearly) {
    t20 = plansData?.map(item_2 => <PlanHighlights key={`${item_2?.id}-highlights`} pricingItem={item_2} yearly={yearly} className={cn("border-b border-neutral-700 first:rounded-bl-xl last:rounded-br-xl first:border-l last:border-r", item_2.mostPopular && "bg-surface-featured")} />);
    $[54] = plansData;
    $[55] = yearly;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== rowClassName || $[58] !== t20) {
    t21 = <div className={rowClassName}>{t20}</div>;
    $[57] = rowClassName;
    $[58] = t20;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== t10 || $[61] !== t13 || $[62] !== t15 || $[63] !== t17 || $[64] !== t19 || $[65] !== t21 || $[66] !== t7 || $[67] !== t8 || $[68] !== t9) {
    t22 = <div ref={t7} className={t8} style={t9} data-test-id={t10}>{t13}{t15}{t17}{t19}{t21}</div>;
    $[60] = t10;
    $[61] = t13;
    $[62] = t15;
    $[63] = t17;
    $[64] = t19;
    $[65] = t21;
    $[66] = t7;
    $[67] = t8;
    $[68] = t9;
    $[69] = t22;
  } else {
    t22 = $[69];
  }
  return t22;
}
function _temp2(t0) {
  const {
    id: id_1,
    button,
    mostPopular: mostPopular_1,
    title: title_0
  } = t0;
  return <PlanCallToAction key={`${id_1}-cta`} button={button} planTitle={title_0 ?? undefined} section={PRICING_CTA_SECTIONS.OVERVIEW} className={cn("border-neutral-700 first:border-l last:border-r", mostPopular_1 && "bg-surface-featured")} linkClassName="lg:whitespace-nowrap xl:flex xl:gap-2.5" />;
}
function _temp(t0) {
  const {
    id: id_0,
    mostPopular: mostPopular_0,
    description
  } = t0;
  return <div key={`${id_0}-description`} className={cn("pb-5 px-3.75", "border-neutral-700 first:border-l last:border-r", mostPopular_0 && "bg-surface-featured")}><Paragraph fontSize="bodyRegularMedium" className="text-neutral-light-100">{description}</Paragraph></div>;
}